import React from 'react';
import { Shield, Lock, Monitor, MonitorSmartphone } from 'lucide-react';
import { getAllProductComparisonDataI18n } from '../config/productComparisonDataI18n';
import { useTranslation } from 'react-i18next';

const ProductComparison = ({ currentProductName }) => {
  const { t } = useTranslation();
  
  // 从配置文件获取所有产品数据（支持多语言）
  const products = getAllProductComparisonDataI18n();

  return (
    <div className="relative bg-gradient-to-b from-gray-50 to-white py-20">
      {/* 装饰性背景元素 - 与新的Shop页面风格保持一致 */}
      <div className="absolute -top-24 -right-24 w-48 h-48 bg-purple-100 rounded-full opacity-20 blur-3xl"></div>
      <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-amber-100 rounded-full opacity-20 blur-3xl"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('productComparison.title')}
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-6">
            {t('productComparison.subtitle')}
          </p>
          <div className="w-24 h-1 bg-purple-500 mx-auto rounded-full"></div>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-purple-500 to-amber-500">
                  <th className="text-left py-4 px-6 font-bold text-white text-base sticky left-0 bg-gradient-to-r from-purple-500 to-purple-500 z-10 min-w-[160px]">
                    {t('productComparison.table.productName')}
                  </th>
                  <th className="text-center py-4 px-6 font-bold text-white text-base min-w-[140px]">
                    {t('productComparison.table.dataPrivacy')}
                  </th>
                  <th className="text-center py-4 px-6 font-bold text-white text-base min-w-[120px]">
                    {t('productComparison.table.warranty')}
                  </th>
                  <th className="text-center py-4 px-6 font-bold text-white text-base min-w-[160px]">
                    {t('productComparison.table.packageIncludes')}
                  </th>
                  <th className="text-center py-4 px-6 font-bold text-white text-base min-w-[100px]">
                    {t('productComparison.table.price')}
                  </th>
                  <th className="text-center py-4 px-6 font-bold text-white text-base min-w-[100px]">
                    {t('productComparison.table.officialPrice')}
                  </th>
                  <th className="text-center py-4 px-6 font-bold text-white text-base min-w-[240px]">
                    {t('productComparison.table.frequencyLimit')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {products.map((product, index) => {
                  const isAppVersion = product.platform === 'web+app';
                  const isEvenRow = index % 2 === 0;
                  
                  return (
                    <tr 
                      key={product.id}
                      className={`
                        border-b border-gray-100 transition-colors duration-300
                        ${isAppVersion ? 'hover:bg-amber-50 bg-amber-50' : 'hover:bg-purple-50'}
                        ${!isAppVersion && !isEvenRow ? 'bg-white' : ''}
                      `}
                    >
                      <td className={`py-4 px-6 font-semibold text-gray-800 sticky left-0 z-10 ${
                        isAppVersion ? 'bg-amber-100' : isEvenRow ? 'bg-gray-50' : 'bg-white'
                      }`}>
                        <div className="text-sm">{product.name}</div>
                        <div className="flex items-center gap-1 mt-1">
                          {product.platform === 'web' ? (
                            <>
                              <Monitor className="w-3.5 h-3.5 text-blue-500" />
                              <span className="text-xs font-medium text-blue-600">{product.platformText}</span>
                            </>
                          ) : (
                            <>
                              <MonitorSmartphone className="w-3.5 h-3.5 text-green-500" />
                              <span className="text-xs font-medium text-green-600">{product.platformText}</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span className="inline-flex items-center text-green-700 text-xs">
                          <Lock className="w-3 h-3 mr-1" />
                          {product.privacy}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <Shield className="w-3 h-3 mr-1" />
                          {product.warranty}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex flex-wrap gap-1 justify-center">
                          {product.includedServices.map((service, idx) => (
                            <span 
                              key={idx}
                              className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
                            >
                              {service}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className={`text-sm font-bold ${
                          isAppVersion ? 'text-purple-700' : 'text-purple-500'
                        }`}>
                          {product.price}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span className="text-sm font-semibold text-red-500">
                          {product.officialPrice}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-wrap gap-1">
                          {product.frequencyLimits.map((limit, idx) => (
                            <span 
                              key={idx}
                              className={`inline-block px-1.5 py-0.5 rounded text-xs ${
                                isAppVersion 
                                  ? 'bg-purple-100 text-purple-700' 
                                  : 'bg-purple-100 text-purple-700'
                              }`}
                            >
                              {limit}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* 当前产品高亮提示 */}
        {currentProductName && (
          <div className="mt-8 text-center">
            <p 
              className="text-gray-600"
              dangerouslySetInnerHTML={{ 
                __html: t('productComparison.currentViewing', { productName: currentProductName })
              }}
            />
          </div>
        )}

      </div>
    </div>
  );
};

export default ProductComparison; 